import clsx from 'clsx';
import { TrendingUp, TrendingDown, Clock } from 'lucide-react';
import type { BetRecommendation } from '../../types';

export interface ConfidenceSnapshot {
  timestamp: string;
  confidence: number;
  edge: number;
  event?: string;
}

interface Props {
  recommendation: BetRecommendation;
  history?: ConfidenceSnapshot[];
}

function MiniSparkline({ snapshots }: { snapshots: ConfidenceSnapshot[] }) {
  if (snapshots.length < 2) return null;
  const width = 200;
  const height = 40;
  const pad = 4;

  const values = snapshots.map((s) => s.confidence);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 0.05);

  const points = snapshots.map((s, i) => {
    const x = pad + (i / (snapshots.length - 1)) * (width - pad * 2);
    const y = pad + (1 - (s.confidence - min) / range) * (height - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  const lastVal = values[values.length - 1];
  const firstVal = values[0];
  const trend = lastVal - firstVal;

  return (
    <div className="flex items-center gap-2">
      <svg width={width} height={height} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke={trend >= 0 ? '#10b981' : '#ef4444'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {snapshots.map((s, i) => {
          const x = pad + (i / (snapshots.length - 1)) * (width - pad * 2);
          const y = pad + (1 - (s.confidence - min) / range) * (height - pad * 2);
          return (
            <circle
              key={i}
              cx={x} cy={y} r={2.5}
              fill={trend >= 0 ? '#10b981' : '#ef4444'}
              opacity={i === snapshots.length - 1 ? 1 : 0.5}
            />
          );
        })}
      </svg>
      <span className={clsx('text-xs font-mono font-semibold', trend >= 0.01 ? 'text-win' : trend <= -0.01 ? 'text-loss' : 'text-gray-400')}>
        {trend >= 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
        {trend >= 0 ? '+' : ''}{(trend * 100).toFixed(1)}%
      </span>
    </div>
  );
}

export function ConfidenceEvolution({ recommendation, history }: Props) {
  const snapshots = history ?? [];
  const latest = recommendation;
  const hasHistory = snapshots.length >= 2;

  return (
    <div className="card p-4 space-y-4">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        <Clock className="w-3 h-3" />
        Confidence Evolution
      </div>

      {/* Current state */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <div className={clsx(
            'text-lg font-mono font-bold',
            latest.confidence >= 0.70 ? 'text-win' : latest.confidence >= 0.55 ? 'text-warn' : 'text-gray-300',
          )}>
            {(latest.confidence * 100).toFixed(0)}%
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">Confidence</div>
        </div>
        <div className="text-center">
          <div className={clsx(
            'text-lg font-mono font-bold',
            latest.edge >= 0.07 ? 'text-win' : latest.edge >= 0.03 ? 'text-warn' : 'text-gray-400',
          )}>
            +{(latest.edge * 100).toFixed(1)}%
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">Edge</div>
        </div>
        <div className="text-center">
          <div className={clsx(
            'text-sm font-mono font-bold',
            latest.decisionStatus === 'STRONG_BET' ? 'text-win' :
            latest.decisionStatus === 'QUALIFIED_BET' ? 'text-warn' :
            'text-gray-400',
          )}>
            {latest.decisionStatus.replace('_', ' ')}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">Status</div>
        </div>
      </div>

      {/* Sparkline */}
      {hasHistory && (
        <div className="pt-3 border-t border-surface-4 space-y-2">
          <div className="text-[10px] text-gray-600 uppercase tracking-wider">Confidence over time</div>
          <MiniSparkline snapshots={snapshots} />
          <div className="flex items-center justify-between text-[10px] text-gray-600">
            <span>{new Date(snapshots[0].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span>Now</span>
          </div>
        </div>
      )}

      {/* Events list */}
      {hasHistory && snapshots.some((s) => s.event) && (
        <div className="space-y-1 pt-2 border-t border-surface-4">
          <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1">Signal Events</div>
          {snapshots
            .filter((s) => s.event)
            .slice(-4)
            .map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-gray-600 shrink-0">
                  {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="text-gray-400 truncate">{s.event}</span>
                <span className={clsx('font-mono text-[11px] shrink-0', s.confidence >= 0.65 ? 'text-win' : 'text-gray-500')}>
                  {(s.confidence * 100).toFixed(0)}%
                </span>
              </div>
            ))}
        </div>
      )}

      {!hasHistory && (
        <div className="text-xs text-gray-600 pt-1">
          Confidence history builds as the market updates closer to game time.
        </div>
      )}
    </div>
  );
}
